import { useRequiredContext } from "@redotech/react-util/context";
import GiftIcon from "@redotech/redo-icons/grid-01.svg";
import HeartIcon from "@redotech/redo-icons/heart.svg";
import StarIcon from "@redotech/redo-icons/star-01.svg";
import UserIcon from "@redotech/redo-icons/user-01.svg";
import {
  LoyaltyEarningRule,
  LoyaltyEarningRuleType,
} from "@redotech/redo-loyalty-common/loyalty-definition";
import {
  EarningRulesBlock,
  LoyaltyBorderStyle,
} from "@redotech/redo-loyalty-common/loyalty-page-builder/definition";
import { assertNever } from "@redotech/util/type";
import { JSXElementConstructor, memo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "../../arbiter-components/featured-icon/redo-featured-icon";
import { RedoDateInput } from "../../arbiter-components/input/redo-date-input";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  LoyaltyCustomer,
  LoyaltyPageContext,
} from "../context/loyalty-page-context";

export const EarningRules = memo(function EarningRules({
  title,
  titleColor,
  subtitle,
  subtitleColor,
  earningRules,
  headerFontFamily,
  bodyFontFamily,
  borderStyle,
}: EarningRulesBlock) {
  const context = useRequiredContext(LoyaltyPageContext);
  const loyaltyEarningRules = context.loyaltyEarningRules.filter(
    (earningRule) => {
      if (earningRule.type === LoyaltyEarningRuleType.REVIEW) {
        return (
          earningRule.enabled && context.supportingProductsStatus.reviewsEnabled
        );
      }
      return earningRule.enabled;
    },
  );

  const completedEarningRules = context.customer?.completedEarningRules;

  return (
    <Flex dir="column" gap="3xl" px="xl" py="6xl" w="full">
      <Text
        fontSize="2xl"
        fontWeight="bold"
        style={{ color: titleColor || "#000000", fontFamily: headerFontFamily }}
      >
        {title || "Ways to Earn Points"}
      </Text>

      <Text
        fontSize="xs"
        style={{ color: subtitleColor, fontFamily: bodyFontFamily }}
      >
        {subtitle ||
          "Complete these activities to earn more points and unlock rewards"}
      </Text>

      <Flex dir="column" gap="3xl" w="full">
        {loyaltyEarningRules.length === 0 ? (
          <Text
            fontSize="lg"
            fontStyle="italic"
            style={{ color: subtitleColor, fontFamily: bodyFontFamily }}
          >
            You've completed all earning activities
          </Text>
        ) : (
          loyaltyEarningRules.map((earningRule) => (
            <EarningRuleTile
              bodyFontFamily={bodyFontFamily}
              borderStyle={borderStyle}
              completed={
                completedEarningRules?.some(
                  (rule) => rule.type === earningRule.type,
                ) ?? false
              }
              earningRule={earningRule}
              key={earningRule.type}
              pointsColor={earningRules.pointsColor}
              subtitleColor={earningRules.subtitleColor}
              titleColor={earningRules.titleColor}
            />
          ))
        )}
      </Flex>
    </Flex>
  );
});

const EarningRuleTile = memo(function EarningRuleTile({
  earningRule,
  completed,
  bodyFontFamily,
  borderStyle,
  titleColor,
  subtitleColor,
  pointsColor,
}: {
  earningRule: LoyaltyEarningRule;
  completed: boolean;
  bodyFontFamily: string;
  borderStyle: LoyaltyBorderStyle;
  titleColor: string;
  subtitleColor: string;
  pointsColor: string;
}) {
  const context = useRequiredContext(LoyaltyPageContext);
  const [isCompleted, setIsCompleted] = useState(completed);
  const [isLoading, setIsLoading] = useState(false);

  const isLoggedIn = !!context.customer;

  const { Icon, title, description } = earningRuleTileMap[earningRule.type];

  const handleCompleteEarningRule = async (rule: LoyaltyEarningRule) => {
    setIsLoading(true);
    try {
      const result = await context.completeEarningRule(rule);
      if (result.success) {
        setIsCompleted(true);
      }
    } catch (error) {
      console.error("Failed to complete earning rule:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const tileAction = getTileAction(
    earningRule,
    context.customer,
    handleCompleteEarningRule,
    isCompleted,
    isLoading,
  );

  return (
    <Flex
      borderColor="primary"
      borderStyle={borderStyle === LoyaltyBorderStyle.BORDER ? "solid" : "none"}
      borderWidth="1px"
      dir="column"
      gap="none"
      p="2xl"
      radius="sm"
      shadow="lg"
    >
      <Flex align="center" dir="row" gap="3xl" justify="space-between" w="full">
        <Flex align="center" dir="row" gap="lg">
          <RedoFeaturedIcon color="gray" Icon={Icon} size="xs" />
          <Flex dir="column" gap="xxs">
            <Text
              fontSize="sm"
              fontWeight="semibold"
              style={{ color: titleColor, fontFamily: bodyFontFamily }}
              textColor="primary"
            >
              {title}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="thin"
              style={{ color: subtitleColor, fontFamily: bodyFontFamily }}
              textColor="tertiary"
            >
              {description}
            </Text>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              style={{ color: pointsColor, fontFamily: bodyFontFamily }}
              textColor="primary"
            >
              {getTilePointDetails(earningRule)}
            </Text>
          </Flex>
        </Flex>
        {isLoggedIn && tileAction}
      </Flex>
    </Flex>
  );
});

function getTilePointDetails(earningRule: LoyaltyEarningRule): string {
  switch (earningRule.type) {
    case LoyaltyEarningRuleType.PURCHASE:
      return `${earningRule.pointsPerDollar} points per dollar spent`;
    case LoyaltyEarningRuleType.ACCOUNT_CREATION:
      return `${earningRule.eventPoints} points`;
    case LoyaltyEarningRuleType.REVIEW:
      return `${earningRule.eventPoints} points`;
    case LoyaltyEarningRuleType.INSTAGRAM:
      return `${earningRule.eventPoints} points`;
    case LoyaltyEarningRuleType.FACEBOOK:
      return `${earningRule.eventPoints} points`;
    case LoyaltyEarningRuleType.TIKTOK:
      return `${earningRule.eventPoints} points`;
    case LoyaltyEarningRuleType.BIRTHDAY:
      return `${earningRule.eventPoints} points`;
    default:
      assertNever(earningRule);
  }
}

function getTileAction(
  earningRule: LoyaltyEarningRule,
  customer: LoyaltyCustomer | null,
  completeEarningRule: (earningRule: LoyaltyEarningRule) => Promise<void>,
  isCompleted: boolean,
  isLoading: boolean,
) {
  const isCompletedButton = (
    <RedoButton disabled hierarchy="primary" text="Completed" />
  );

  switch (earningRule.type) {
    case LoyaltyEarningRuleType.PURCHASE: {
      return null;
    }
    case LoyaltyEarningRuleType.ACCOUNT_CREATION: {
      return isCompleted ? (
        isCompletedButton
      ) : (
        <RedoButton
          disabled={isLoading}
          hierarchy="primary"
          onClick={async () => {
            await completeEarningRule(earningRule);
          }}
          text={isLoading ? "Claiming..." : "Claim"}
        />
      );
    }
    case LoyaltyEarningRuleType.REVIEW: {
      if (isCompleted) {
        return isCompletedButton;
      }

      if (customer?.earningRuleConditions?.hasReviewed) {
        return (
          <RedoButton
            disabled={isLoading}
            hierarchy="primary"
            onClick={async () => {
              await completeEarningRule(earningRule);
            }}
            text={isLoading ? "Claiming..." : "Claim"}
          />
        );
      } else {
        return (
          <RedoButton disabled hierarchy="primary" text="Write a review" />
        );
      }
    }
    case LoyaltyEarningRuleType.INSTAGRAM: {
      if (isCompleted) {
        return isCompletedButton;
      }

      return (
        <RedoButton
          disabled={isLoading}
          hierarchy="primary"
          onClick={async () => {
            if (earningRule.instagramLink) {
              window.open(earningRule.instagramLink, "_blank");
            }
            await completeEarningRule(earningRule);
          }}
          text={isLoading ? "Following..." : "Follow"}
        />
      );
    }
    case LoyaltyEarningRuleType.FACEBOOK: {
      if (isCompleted) {
        return isCompletedButton;
      }

      return (
        <RedoButton
          disabled={isLoading}
          hierarchy="primary"
          onClick={async () => {
            if (earningRule.facebookLink) {
              window.open(earningRule.facebookLink, "_blank");
            }
            await completeEarningRule(earningRule);
          }}
          text={isLoading ? "Liking..." : "Like"}
        />
      );
    }
    case LoyaltyEarningRuleType.TIKTOK: {
      if (isCompleted) {
        return isCompletedButton;
      }

      return (
        <RedoButton
          disabled={isLoading}
          hierarchy="primary"
          onClick={async () => {
            if (earningRule.tiktokLink) {
              window.open(earningRule.tiktokLink, "_blank");
            }
            await completeEarningRule(earningRule);
          }}
          text={isLoading ? "Following..." : "Follow"}
        />
      );
    }
    case LoyaltyEarningRuleType.BIRTHDAY: {
      if (isCompleted) {
        return isCompletedButton;
      }

      return (
        <BirthdayTile
          completeEarningRule={completeEarningRule}
          customer={customer}
          earningRule={earningRule}
          isLoading={isLoading}
        />
      );
    }
    default:
      assertNever(earningRule);
  }
}

const earningRuleTileMap: Record<
  LoyaltyEarningRuleType,
  { Icon: JSXElementConstructor<any>; title: string; description: string }
> = {
  [LoyaltyEarningRuleType.PURCHASE]: {
    Icon: GiftIcon,
    title: "Make a purchase",
    description: "Points earned per dollar spent",
  },
  [LoyaltyEarningRuleType.ACCOUNT_CREATION]: {
    Icon: UserIcon,
    title: "Create an account",
    description: "Points awarded upon account creation",
  },
  [LoyaltyEarningRuleType.REVIEW]: {
    Icon: StarIcon,
    title: "Write a review",
    description: "Points earned for writing a product review",
  },
  [LoyaltyEarningRuleType.INSTAGRAM]: {
    Icon: HeartIcon,
    title: "Follow on Instagram",
    description: "Points earned for following on Instagram",
  },
  [LoyaltyEarningRuleType.FACEBOOK]: {
    Icon: HeartIcon,
    title: "Like on Facebook",
    description: "Points earned for liking on Facebook",
  },
  [LoyaltyEarningRuleType.TIKTOK]: {
    Icon: HeartIcon,
    title: "Follow on TikTok",
    description: "Points earned for following on TikTok",
  },
  [LoyaltyEarningRuleType.BIRTHDAY]: {
    Icon: HeartIcon,
    title: "Birthday",
    description: "Points awarded on your birthday",
  },
};

const BirthdayTile = memo(function BirthdayTile({
  earningRule,
  customer,
  isLoading,
  completeEarningRule,
}: {
  earningRule: LoyaltyEarningRule;
  customer: LoyaltyCustomer | null;
  isLoading: boolean;
  completeEarningRule: (earningRule: LoyaltyEarningRule) => Promise<void>;
}) {
  const context = useRequiredContext(LoyaltyPageContext);
  const [birthday, setBirthday] = useState<Temporal.PlainDate | null>(null);
  const [error, setError] = useState<string | null>(null);

  // If customer already has birthday set, show it's recorded
  if (customer?.earningRuleConditions?.hasBirthday) {
    return (
      <RedoButton disabled hierarchy="tertiary" text="Birthday Recorded" />
    );
  }

  const validateAndSaveBirthday = async () => {
    if (!birthday) {
      setError("Please select a birthday");
      return;
    }

    const today = Temporal.Now.plainDateISO();
    if (Temporal.PlainDate.compare(birthday, today) > 0) {
      setError("Birthday cannot be in the future");
      return;
    }

    setError(null);

    try {
      const jsDate = new Date(birthday.year, birthday.month - 1, birthday.day);
      const result = await context.updateBirthday(jsDate);
      if (result.success) {
        await completeEarningRule(earningRule);
        window.location.reload();
      } else {
        setError("Failed to save birthday. Please try again.");
      }
    } catch (error) {
      console.error("Failed to update birthday:", error);
      setError("Failed to save birthday. Please try again.");
    }
  };

  return (
    <Flex dir="column" gap="sm">
      <Flex align="flex-end" dir="row" gap="sm">
        <RedoDateInput
          errorText={error || undefined}
          label="Birthday"
          setValue={(value) => {
            setBirthday(value);
            setError(null);
          }}
          size="sm"
          state={error ? "error" : undefined}
          value={birthday}
        />
        <RedoButton
          disabled={isLoading}
          hierarchy="primary"
          onClick={validateAndSaveBirthday}
          size="sm"
          text={isLoading ? "Saving..." : "Save"}
        />
      </Flex>
    </Flex>
  );
});
